import { graphql, readInlineData } from "react-relay";

import { article as articleData } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type { InlineAdSlotSchemaWithRow } from "scmp-app/components/schema-render/common/inline-ad-slot/schemas";
import type { helpersCheckArticleIsInSectionsArticle$key } from "scmp-app/queries/__generated__/helpersCheckArticleIsInSectionsArticle.graphql";
import type { helpersCheckArticleRenderTypeArticle$key } from "scmp-app/queries/__generated__/helpersCheckArticleRenderTypeArticle.graphql";
import type { helpersCheckHasLiveTagArticle$key } from "scmp-app/queries/__generated__/helpersCheckHasLiveTagArticle.graphql";
import type { helpersGetArticleTypeArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleTypeArticle.graphql";

import {
  checkCanShowInPlusPlatform,
  checkIsContainsPlusQueryString,
  checkIsPlusArticle,
} from "./plus/helpers";
import { checkIsPostMagazineArticle } from "./post-magazine-article/helper";
import { checkIsStyleArticle } from "./style-article/helper";
import { ArticleRenderType } from "./types";

export const getArticleType = (reference: helpersGetArticleTypeArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersGetArticleTypeArticle on Content @inline {
        ... on Article {
          types {
            value {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );
  const [articleType] = article.types?.flatMap(type => type?.value) ?? [];
  return articleType?.entityId;
};

export const checkArticleIsInSections = (
  reference: helpersCheckArticleIsInSectionsArticle$key,
  entityIds: string[],
  options?: { isCheckArticleFirstSectionOnly?: boolean },
) => {
  const { isCheckArticleFirstSectionOnly = false } = options ?? {};
  const article = readInlineData(
    graphql`
      fragment helpersCheckArticleIsInSectionsArticle on Content @inline {
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );

  const targetArticleSections = isCheckArticleFirstSectionOnly
    ? article?.sectionsV2?.slice(0, 1)
    : article?.sectionsV2;
  const flattenArticleSectionSegments =
    targetArticleSections?.flatMap(
      section => section?.value?.map(value => value && value.entityId).filter(notEmpty) ?? [],
    ) ?? [];
  return entityIds.some(entityId => flattenArticleSectionSegments.includes(entityId));
};

export const checkHasLiveTag = (reference: helpersCheckHasLiveTagArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckHasLiveTagArticle on Article @inline {
        flattenTypeIds
        liveArticle {
          status
        }
      }
    `,
    reference,
  );
  const isLiveArticle =
    article.flattenTypeIds?.includes(articleData.types.live.entityUuid) ?? false;
  const isLive = article.liveArticle?.status ?? false;
  return isLiveArticle && isLive;
};

export const checkArticleRenderType = (
  reference: helpersCheckArticleRenderTypeArticle$key,
  query: Record<string, string | string[] | undefined>,
) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckArticleRenderTypeArticle on Article @inline {
        ...helpersCheckCanShowInPlusPlatformContent
        ...helpersCheckIsPlusArticle
        ...helpersCheckIsPostMagazineArticle
        ...helpersCheckIsStyleArticle
        ...helpersGetArticleTypeArticle
      }
    `,
    reference,
  );

  if (checkIsStyleArticle(article)) return ArticleRenderType.Style;
  else if (
    checkIsPlusArticle(article) ||
    (checkCanShowInPlusPlatform(article) && checkIsContainsPlusQueryString(query))
  )
    return ArticleRenderType.Plus;
  else if (checkIsPostMagazineArticle(article)) return ArticleRenderType.PostMagazine;

  const articleType = getArticleType(article);

  switch (articleType) {
    case articleData.types.infographic.entityId:
      return ArticleRenderType.Infographic;
    case articleData.types.infographicLongRead.entityId:
      return ArticleRenderType.InfographicLongRead;
    case articleData.types.live.entityId:
      return ArticleRenderType.Live;
    case articleData.types.longRead.entityId:
      return ArticleRenderType.LongRead;
    case articleData.types.newsletter.entityId:
      return ArticleRenderType.Newsletter;
    case articleData.types.photoEssay.entityId:
      return ArticleRenderType.PhotoEssay;
    case articleData.types.series.entityId:
      return ArticleRenderType.Series;
    case articleData.types.studio.entityId:
      return ArticleRenderType.MorningStudio;
    default:
      return ArticleRenderType.Generic;
  }
};

type GetSchemaWithUpdatedAdSlotParameters = {
  inlineAdSlotSchemaList: InlineAdSlotSchemaWithRow[];
  schema: Schema;
};

// in pwa v1 codebase, https://project.scmp.tech/product/web/scmp-pwa/-/blob/1deee0c7d5e467f4a37fe08aba52f77b77e0d1af/scmp-app/src/universal/components/article/Studio/index.vue#L109
// the ad slot position is rendered together with the paragraph. No need to +1
// but in pwa v2 codebase, we insert the ad slot into the schema as an array element,
// the ad slot is not rendered together with the paragraph. So, we add to +1 to the adSlotPosition
// to make sure the ad slot is after the desired paragraph.
export const getSchemaWithUpdatedAdSlot = ({
  inlineAdSlotSchemaList,
  schema,
}: GetSchemaWithUpdatedAdSlotParameters) => {
  const getHasHardLinkChild = (node: SchemaNode) =>
    node.children?.[0]?.type === "a" && node.children.length === 1;
  const getIsTextParagraph = (node: SchemaNode) => node.type === "p" && !getHasHardLinkChild(node);

  // this ad-slot position finding logic is different from the one in content service
  // as advised by the PM, we use a different logic for morning studio articles
  // https://scmp.slack.com/archives/C02S11PDGMD/p1679475941818559
  // https://project.scmp.tech/product/web/scmp-pwa/-/blob/1deee0c7d5e467f4a37fe08aba52f77b77e0d1af/scmp-app/src/universal/components/article/Studio/index.vue#L720
  const findAdSlotPosition = (schema: Schema, position: number) => {
    let slotCount = 0;
    return schema.findIndex(node => {
      if (getIsTextParagraph(node)) slotCount++;
      return slotCount >= position;
    });
  };

  const newSchema = schema.filter(node => node.type !== "inline-ad-slot");
  inlineAdSlotSchemaList.forEach(inlineAdSlotSchema => {
    const position = findAdSlotPosition(newSchema, inlineAdSlotSchema.row);
    if (position > 0) newSchema.splice(position + 1, 0, inlineAdSlotSchema);

    /**
     * The last element in the schema is track-viewed-percentage 100.
     * The ad slot with row -1 means it should be rendered at the end of the article,
     * but before the track-viewed-percentage node.
     */
    if (inlineAdSlotSchema.row === -1) newSchema.splice(-1, 0, inlineAdSlotSchema);
  });
  return newSchema;
};
